import { FirestoreService } from '../../../shared/services/firestoreService';
import { useDashboardDiagramStore } from './dashboardStore';
import { addStatus, deleteStatus } from '../../../shared/status/statusActions';
import { DiagramModel } from '@diagram/model/diagram.model';
import { useOrganizationStore } from '../../../shared/organization/organizationStore';
import { queryDocuments } from '../../../shared/services/firestoreUtilities';
import { getFirestoreInstance } from '../../../shared/services/firebaseConfig';
import { OrganizationUser } from '../../../shared/organization/types';
import { rtdbDiagramRef } from '@diagram/service/diagram.rtdb';
//  Instantiate FirestoreService
const diagramService = new FirestoreService<DiagramModel>('diagrams');
let isFetching = false;

export const queryDiagrams = async () => {
  const store = useDashboardDiagramStore.getState();
  const orgStore = useOrganizationStore.getState();
  const { where } = await import('firebase/firestore');
  const firestore = await getFirestoreInstance();
  if (isFetching) return;
  isFetching = true;
  if (!orgStore.organization) {
    addStatus({
      type: 'error',
      message: 'Organization not found',
      autoDismiss: true,
    });
    return;
  }
  const statusID = await addStatus({
    type: 'loading',
    message: 'Fetching diagrams...',
    autoDismiss: false,
  });

  try {
    const { documents, total } = await queryDocuments<DiagramModel>(
      firestore,
      'diagrams',
      where('organizationId', '==', orgStore?.organization?.id),
    );

    store.setItems(documents);
    store.setTotalItems(total);
    if (total > 0) {
      addStatus({
        type: 'success',
        message: 'Diagrams fetched successfully',
        autoDismiss: true,
      });
    } else {
      addStatus({
        type: 'info',
        message: 'No diagrams found',
        autoDismiss: true,
      });
    }
  } catch (error) {
    console.error('Error fetching diagrams:', error);
    addStatus({
      type: 'error',
      message: 'Error fetching diagrams',
      autoDismiss: true,
    });
  } finally {
    isFetching = false;
    deleteStatus(statusID);
  }
};

// Query Diagrams
// export const queryDiagrams = async (
//   searchTerm: string = '',
//   goBack: boolean = false,
//   pageSize: number = 10
// ) => {
//   const store = useDashboardDiagramStore.getState();
//   if (isFetching) return;
//   isFetching = true;
//   const statusID = await addStatus({
//     type: 'loading',
//     message: 'Fetching diagrams...',
//     autoDismiss: false,
//   });
//   try {
//     const { data } = await diagramService.queryAll(
//       searchTerm,
//       'name',
//       pageSize,
//       goBack
//     );

//     const totalResults = await diagramService.getTotalCount();

//     store.setItems(data);
//     store.setTotalItems(totalResults);
//   } catch (error) {
//     console.error('Error fetching diagrams:', error);
//     addStatus({
//       type: 'error',
//       message: 'Error fetching diagrams',
//       autoDismiss: true,
//     });
//   } finally {
//     isFetching = false;
//     deleteStatus(statusID);
//     addStatus({
//       type: 'success',
//       message: 'Diagrams fetched successfully',
//       autoDismiss: true,
//     });
//   }
// };

// ✅ Reset pagination and clear cache
export const resetDiagramPagination = () => {
  diagramService.resetPagination();
};

//  Start Real-Time Updates
export const listenToDiagrams = () => {
  return diagramService.listenForUpdates((diagrams) => {
    useDashboardDiagramStore.getState().setItems(diagrams);
  });
};

export const stopListeningToDiagrams = () => {
  diagramService.stopListening();
};

//  Create a New Diagram (Optimistic UI)
export const addDiagram = async (
  diagramData: Omit<DiagramModel, 'id' | 'settings' | 'organizationId'>,
) => {
  try {
    const store = useDashboardDiagramStore.getState();
    const orgStore = useOrganizationStore.getState();
    const tempId = `temp-${Date.now()}`;
    const { set } = await import('firebase/database');
    if (!diagramData.name) throw new Error('Diagram name is required');
    if (!diagramData.users) throw new Error('Users Required');
    if (!orgStore.organization) throw new Error('Organization not found');

    const diagram = {
      name: diagramData.name,
      organizationId: orgStore.organization?.id,
      description: diagramData.description || '',
      users: diagramData.users,
      access: diagramData.access || 'public',
      settings: {
        snapToGrid: true,
        autoSpacing: false,
        gridSize: 20,
        theme: 'light',
        nodeDistance: 150,
      },
    } as DiagramModel;

    // **Optimistic UI: Add temporary diagram**
    store.addItem({ ...diagram, id: tempId });

    try {
      const messageId = addStatus({
        type: 'loading',
        message: 'Creating diagram...',
        autoDismiss: false,
      });
      const diagramId = await diagramService.create(diagram);
      const rtdbRef = await rtdbDiagramRef(diagramId, '');
      await set(rtdbRef, {
        ...diagram,
        id: diagramId,
        createdAt: Date.now(),
      });
      // **Update to real ID**
      const updatedItems = { ...store.items };
      updatedItems[diagramId] = { ...diagram, id: diagramId };
      delete updatedItems[tempId];
      store.setItems(Object.values(updatedItems));
      addStatus({
        type: 'success',
        message: 'Diagram created successfully',
        autoDismiss: true,
      });
      deleteStatus(messageId);
      return diagramId;
    } catch (error) {
      console.error('Error creating diagram:', error);
      addStatus({
        type: 'error',
        message: 'Error creating diagram',
        autoDismiss: true,
      });
      // **Rollback UI if Firestore fails**
      store.deleteItem(tempId);
    }
  } catch (error) {
    console.error('Error creating diagram:', error);
    throw error;
  }
};

//  Update Diagram (Optimistic UI)
export const updateDiagram = async (
  diagram: Partial<DiagramModel> & { id: string },
) => {
  const store = useDashboardDiagramStore.getState();
  const previousData = store.items[diagram.id];

  // **Optimistic UI: Update immediately**
  store.updateItem(diagram);

  try {
    await diagramService.update(diagram.id, diagram);
    addStatus({
      type: 'success',
      message: 'Diagram updated successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error updating diagram:', error);
    addStatus({
      type: 'error',
      message: 'Error updating diagram',
      autoDismiss: true,
    });
    // **Rollback UI if Firestore fails**
    store.updateItem(previousData);
  }
};

//  Delete Diagram (Optimistic UI)
export const deleteDiagram = async (id: string) => {
  const store = useDashboardDiagramStore.getState();
  const previousData = store.items[id];

  // **Optimistic UI: Remove immediately**
  store.deleteItem(id);

  try {
    await diagramService.delete(id);
    addStatus({
      type: 'success',
      message: 'Diagram deleted successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error deleting diagram:', error);
    addStatus({
      type: 'error',
      message: 'Error deleting diagram',
      autoDismiss: true,
    });
    // **Rollback UI if Firestore fails**
    store.addItem(previousData);
  }
};

export const diagramPermissionCheck = (
  requiredRole: string,
  userId: string,
  users: { [key: string]: string },
) => {
  const userRole = users[userId];
  if (!userRole) {
    return false;
  }
  const roles = ['viewer', 'editor', 'author', 'admin'];
  const requiredRoleIndex = roles.indexOf(requiredRole);
  const userRoleIndex = roles.indexOf(userRole);

  if (userRoleIndex >= requiredRoleIndex) {
    return true;
  } else {
    return false;
  }
};

export const dashboardPermissionCheck = (
  requiredRole: string,
  userId: string,
  users: { [key: string]: OrganizationUser },
) => {
  const org = useOrganizationStore.getState().organization;
  const user = users[`${userId}_${org?.id}`];
  if (!user) {
    return false;
  }

  const roles = ['viewer', 'admin', 'owner'];
  const requiredRoleIndex = roles.indexOf(requiredRole);
  const userRoleIndex = roles.indexOf(user.role);

  if (userRoleIndex >= requiredRoleIndex) {
    return true;
  } else {
    return false;
  }
};
